import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Alert, Typography, Result } from 'antd';
import { LockOutlined, EyeInvisibleOutlined, EyeTwoTone, CheckCircleOutlined } from '@ant-design/icons';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;

const ResetPassword = () => {
  const [form] = Form.useForm();
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/verify-reset-token/${token}`
      );
      
      if (response.data.success) {
        setTokenValid(true);
      } else {
        setTokenValid(false);
        setError('Invalid or expired reset link');
      }
    } catch (err) {
      setTokenValid(false);
      setError(err.response?.data?.message || 'Invalid or expired reset link');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/reset-password/${token}`,
        { password: values.password }
      );

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/auth/login');
        }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <Text className="text-gray-600 dark:text-gray-400">Verifying reset link...</Text>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 w-full">
        <div className="flex min-h-screen w-full">
          {/* Left Column - Logo and Branding */}
          <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-green-600 to-green-800 relative overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-5">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }} />
            </div>
            
            <div className="relative z-10 flex flex-col justify-center items-center w-full px-8 xl:px-12">
              <div className="mb-12">
                <img 
                  src="/qqcrm-logo.png" 
                  alt="QQCRM Logo" 
                  className="h-16 xl:h-20 w-auto filter brightness-0 invert"
                />
              </div>
              
              <div className="text-center text-white max-w-md">
                <div className="mb-6">
                  <CheckCircleOutlined style={{ fontSize: 64, color: '#fff' }} />
                </div>
                <h1 className="text-3xl xl:text-4xl font-bold mb-6 leading-tight">
                  Password Reset Successful!
                </h1>
                <p className="text-lg xl:text-xl text-green-100 leading-relaxed">
                  Your password has been successfully reset. You can now log in with your new password.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Success Message */}
          <div className="w-full lg:w-1/2 flex flex-col">
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-8 py-6">
              <div className="text-center lg:text-left">
                <div className="lg:hidden mb-6">
                  <img 
                    src="/qqcrm-logo.png" 
                    alt="QQCRM Logo" 
                    className="h-12 w-auto mx-auto"
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 bg-white dark:bg-gray-800 px-4 sm:px-6 lg:px-8 py-8 overflow-y-auto w-full flex items-center justify-center">
              <div className="w-full max-w-md">
                <Result
                  status="success"
                  title="Password Reset Complete!"
                  subTitle={
                    <div className="space-y-4">
                      <Paragraph className="text-gray-600 dark:text-gray-400">
                        Your password has been successfully reset.
                      </Paragraph>
                      <Paragraph className="text-gray-600 dark:text-gray-400">
                        Redirecting to login page in 3 seconds...
                      </Paragraph>
                    </div>
                  }
                  extra={[
                    <Link to="/auth/login" key="login">
                      <Button type="primary" size="large">
                        Go to Login
                      </Button>
                    </Link>
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 w-full">
        <div className="flex min-h-screen w-full items-center justify-center">
          <div className="w-full max-w-md px-4">
            <Result
              status="error"
              title="Invalid Reset Link"
              subTitle={error || "This password reset link is invalid or has expired. Please request a new one."}
              extra={[
                <Link to="/forgot-password" key="forgot">
                  <Button type="primary" size="large">
                    Request New Link
                  </Button>
                </Link>,
                <Link to="/auth/login" key="login">
                  <Button size="large">
                    Back to Login
                  </Button>
                </Link>
              ]}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 w-full">
      <div className="flex min-h-screen w-full">
        {/* Left Column - Logo and Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-blue-800 relative overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>
          
          <div className="relative z-10 flex flex-col justify-center items-center w-full px-8 xl:px-12">
            <div className="mb-12">
              <img 
                src="/qqcrm-logo.png" 
                alt="QQCRM Logo" 
                className="h-16 xl:h-20 w-auto filter brightness-0 invert"
              />
            </div>
            
            <div className="text-center text-white max-w-md">
              <h1 className="text-3xl xl:text-4xl font-bold mb-6 leading-tight">
                Create New Password
              </h1>
              <p className="text-lg xl:text-xl text-blue-100 mb-10 leading-relaxed">
                Choose a strong password to secure your account.
              </p>
              
              <div className="space-y-5 text-left">
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-blue-300 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 text-base">At least 8 characters</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-blue-300 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 text-base">Mix of letters & numbers</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-blue-300 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-100 text-base">Secure & Encrypted</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Form */}
        <div className="w-full lg:w-1/2 flex flex-col">
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-8 py-6">
            <div className="text-center lg:text-left">
              <div className="lg:hidden mb-6">
                <img 
                  src="/qqcrm-logo.png" 
                  alt="QQCRM Logo" 
                  className="h-12 w-auto mx-auto"
                />
              </div>
              
              <Title level={2} className="text-gray-900 dark:text-gray-100 mb-2">
                Reset Your Password
              </Title>
              <Text className="text-gray-600 dark:text-gray-400 text-lg">
                Enter your new password below
              </Text>
            </div>
          </div>

          <div className="flex-1 bg-white dark:bg-gray-800 px-4 sm:px-6 lg:px-8 py-8 overflow-y-auto w-full flex items-center justify-center">
            <div className="w-full max-w-md">
              {error && (
                <Alert
                  message="Error"
                  description={error}
                  type="error"
                  showIcon
                  closable
                  onClose={() => setError(null)}
                  className="mb-6"
                />
              )}

              <Form
                form={form}
                name="reset-password"
                onFinish={handleSubmit}
                layout="vertical"
                size="large"
                autoComplete="off"
              >
                <Form.Item
                  name="password"
                  label="New Password"
                  rules={[
                    { required: true, message: 'Please enter your new password' },
                    { min: 8, message: 'Password must be at least 8 characters' },
                    {
                      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                      message: 'Password must contain uppercase, lowercase, and number'
                    }
                  ]}
                  hasFeedback
                >
                  <Input.Password
                    prefix={<LockOutlined className="text-gray-400" />}
                    placeholder="Enter new password"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                    autoFocus
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  label="Confirm Password"
                  dependencies={['password']}
                  hasFeedback
                  rules={[
                    { required: true, message: 'Please confirm your password' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Passwords do not match'));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined className="text-gray-400" />}
                    placeholder="Confirm new password"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    className="w-full h-12 text-base font-medium"
                  >
                    {loading ? 'Resetting Password...' : 'Reset Password'}
                  </Button>
                </Form.Item>

                <div className="text-center">
                  <Link 
                    to="/auth/login" 
                    className="text-primary-600 hover:text-primary-500 font-medium"
                  >
                    Back to Login
                  </Link>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
